import React, { useState } from 'react';
import { extractApiError } from '../../utils/errorMessages';
import { createBenchmark, updateBenchmark } from '../../services/paperPricingService';
import {
    BATE_LABELS,
    PRICE_POINTS,
    PROVIDERS,
    REGIONS,
    fieldErrorsFrom,
    unplacedFieldErrors,
} from './paperPricingFormat';
import { FormField } from './PaperPricingParts';
import PaperPricingModal from './PaperPricingModal';

const FORM_FIELDS = [
    'name', 'code', 'description', 'provider', 'default_bate', 'product', 'location',
    'region', 'basis', 'currency', 'unit', 'valid_from', 'price_source',
];

const BLANK = {
    name: '',
    code: '',
    description: '',
    provider: '',
    default_bate: 'c',
    product: '',
    location: '',
    region: '',
    basis: '',
    currency: 'USD',
    unit: '',
    valid_from: '',
    price_source: '',
};

// Optional text fields arrive as '' or null; the inputs need ''.
const fromBenchmark = (benchmark) => Object.fromEntries(
    FORM_FIELDS.map((field) => [field, benchmark[field] ?? '']),
);

// Codes, units and currencies are capital letters and digits with no spaces:
// that is how the price file spells them, and how the server stores them.
const asCode = (text) => text.toUpperCase().replace(/\s+/g, '');

/**
 * Add or edit a benchmark index (ledger row 132, AC2).
 *
 * The code is how a price file finds its benchmark, so it is typed once and
 * never changes: read-only when editing and not sent at all. A code already on
 * the list is refused by the server, naming the benchmark that has it; that
 * sentence is shown under the code as the server wrote it.
 */
const BenchmarkFormModal = ({ benchmark, onClose, onSaved }) => {
    const editing = Boolean(benchmark);
    const [form, setForm] = useState(() => (editing ? fromBenchmark(benchmark) : BLANK));
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
    const setUpper = (field) => (event) => setForm((current) => ({ ...current, [field]: asCode(event.target.value) }));

    const submit = async (event) => {
        event.preventDefault();
        const problems = {};
        if (!form.name.trim()) problems.name = 'Enter the name the desk knows this price by.';
        if (!editing && !form.code) problems.code = 'Enter the code as the price file writes it, e.g. AMFSA00.';
        if (!form.provider) problems.provider = 'Pick who publishes this price.';
        if (!form.currency) problems.currency = 'Enter the currency, e.g. USD.';
        if (!form.unit) problems.unit = 'Enter the unit the price is quoted per, e.g. MT.';
        setErrors(problems);
        setFormError('');
        if (Object.keys(problems).length) return;

        const payload = {
            name: form.name.trim(),
            description: form.description.trim(),
            provider: form.provider,
            default_bate: form.default_bate,
            product: form.product.trim(),
            location: form.location.trim(),
            region: form.region,
            basis: form.basis.trim(),
            currency: form.currency,
            unit: form.unit,
            // Optional: no date is "not set", not a made-up one.
            valid_from: form.valid_from || null,
            price_source: form.price_source.trim(),
        };

        setSaving(true);
        try {
            const saved = editing
                ? await updateBenchmark(benchmark.id, payload)
                : await createBenchmark({ ...payload, code: form.code });
            onSaved(saved, editing);
        } catch (err) {
            const fields = fieldErrorsFrom(err);
            if (fields) {
                setErrors(fields);
                setFormError(unplacedFieldErrors(fields, FORM_FIELDS) || 'Fix the fields marked below, then save again.');
            } else {
                setFormError(extractApiError(err, 'The benchmark could not be saved. Try again.'));
            }
            setSaving(false);
        }
    };

    const footer = (
        <>
            <button type="button" className="pp-btn pp-btn--secondary" onClick={onClose} disabled={saving}>
                Cancel
            </button>
            <button type="submit" className="pp-btn pp-btn--primary" disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add benchmark'}
            </button>
        </>
    );

    return (
        <PaperPricingModal
            title={editing ? `Edit ${benchmark.code}` : 'Add benchmark'}
            eyebrow="Benchmark indexes"
            onClose={onClose}
            busy={saving}
            wide
            onSubmit={submit}
            footer={footer}
        >
            {formError ? <div className="pp-alert pp-alert--error" role="alert">{formError}</div> : null}

            <div className="pp-form-grid">
                <div className="pp-form-wide">
                    <FormField label="Display name" htmlFor="pp-bm-name" required error={errors.name}>
                        <input
                            id="pp-bm-name"
                            className="pp-input"
                            value={form.name}
                            onChange={set('name')}
                            placeholder="e.g. Marine Fuel 0.5% Singapore"
                            maxLength={200}
                            disabled={saving}
                        />
                    </FormField>
                </div>

                <FormField
                    label="Code"
                    htmlFor="pp-bm-code"
                    required={!editing}
                    error={errors.code}
                    hint={editing
                        ? "The code is how price files find this benchmark, so it can't be changed. Add a new benchmark instead."
                        : "As the price file writes it, e.g. AMFSA00. It can't be changed later."}
                >
                    <input
                        id="pp-bm-code"
                        className="pp-input pp-input--code"
                        value={form.code}
                        onChange={setUpper('code')}
                        maxLength={20}
                        disabled={editing || saving}
                        autoComplete="off"
                    />
                </FormField>

                <FormField label="Provider" htmlFor="pp-bm-provider" required error={errors.provider}>
                    <select
                        id="pp-bm-provider"
                        className="pp-select"
                        value={form.provider}
                        onChange={set('provider')}
                        disabled={saving}
                    >
                        <option value="">Pick a provider…</option>
                        {PROVIDERS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </FormField>

                <div className="pp-form-wide">
                    <FormField label="Official description" htmlFor="pp-bm-description" error={errors.description}>
                        <input
                            id="pp-bm-description"
                            className="pp-input"
                            value={form.description}
                            onChange={set('description')}
                            placeholder="e.g. Marine Fuel 0.5% FOB Spore Cargo $/mt"
                            maxLength={255}
                            disabled={saving}
                        />
                    </FormField>
                </div>

                <FormField label="Product" htmlFor="pp-bm-product" error={errors.product}>
                    <input
                        id="pp-bm-product"
                        className="pp-input"
                        value={form.product}
                        onChange={set('product')}
                        placeholder="e.g. VLSFO 0.5%S"
                        maxLength={100}
                        disabled={saving}
                    />
                </FormField>

                <FormField label="Location" htmlFor="pp-bm-location" error={errors.location}>
                    <input
                        id="pp-bm-location"
                        className="pp-input"
                        value={form.location}
                        onChange={set('location')}
                        placeholder="e.g. Singapore"
                        maxLength={100}
                        disabled={saving}
                    />
                </FormField>

                <FormField label="Region" htmlFor="pp-bm-region" error={errors.region}>
                    <select
                        id="pp-bm-region"
                        className="pp-select"
                        value={form.region}
                        onChange={set('region')}
                        disabled={saving}
                    >
                        <option value="">Not set</option>
                        {REGIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </FormField>

                <FormField label="Basis" htmlFor="pp-bm-basis" error={errors.basis}>
                    <input
                        id="pp-bm-basis"
                        className="pp-input"
                        value={form.basis}
                        onChange={set('basis')}
                        placeholder="e.g. FOB Cargo"
                        maxLength={100}
                        disabled={saving}
                    />
                </FormField>

                <FormField
                    label="Currency"
                    htmlFor="pp-bm-currency"
                    required
                    error={errors.currency}
                    hint="Three letters, e.g. USD."
                >
                    <input
                        id="pp-bm-currency"
                        className="pp-input pp-input--code"
                        value={form.currency}
                        onChange={setUpper('currency')}
                        maxLength={3}
                        disabled={saving}
                        autoComplete="off"
                    />
                </FormField>

                <FormField
                    label="Quoted unit"
                    htmlFor="pp-bm-unit"
                    required
                    error={errors.unit}
                    hint="The unit the price is quoted per, e.g. MT or BBL."
                >
                    <input
                        id="pp-bm-unit"
                        className="pp-input pp-input--code"
                        value={form.unit}
                        onChange={setUpper('unit')}
                        maxLength={10}
                        disabled={saving}
                        autoComplete="off"
                    />
                </FormField>

                <FormField
                    label="Default price point"
                    htmlFor="pp-bm-bate"
                    error={errors.default_bate}
                    hint="The price point a new floating formula starts on. Close for nearly every contract."
                >
                    <select
                        id="pp-bm-bate"
                        className="pp-select"
                        value={form.default_bate}
                        onChange={set('default_bate')}
                        disabled={saving}
                    >
                        {PRICE_POINTS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                        {/* A saved default outside Close / High / Low stays visible as it is. */}
                        {PRICE_POINTS.some((option) => option.value === form.default_bate) ? null : (
                            <option value={form.default_bate}>
                                {BATE_LABELS[form.default_bate] || form.default_bate}
                            </option>
                        )}
                    </select>
                </FormField>

                <FormField label="Valid from" htmlFor="pp-bm-valid-from" error={errors.valid_from}>
                    <input
                        id="pp-bm-valid-from"
                        type="date"
                        className="pp-input"
                        value={form.valid_from}
                        onChange={set('valid_from')}
                        disabled={saving}
                    />
                </FormField>

                <div className="pp-form-wide">
                    <FormField label="Price source" htmlFor="pp-bm-source" error={errors.price_source}>
                        <input
                            id="pp-bm-source"
                            className="pp-input"
                            value={form.price_source}
                            onChange={set('price_source')}
                            placeholder="e.g. Platts Feed"
                            maxLength={100}
                            disabled={saving}
                        />
                    </FormField>
                </div>
            </div>
        </PaperPricingModal>
    );
};

export default BenchmarkFormModal;
